import { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

// ─── Animation Wrappers ────────────────────────────────────────────────────────

const AnimatedSection = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerContainer = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.15 } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerItem = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 30 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isEventPast, setIsEventPast] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const targetDate = new Date('2026-04-02T08:00:00');

    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();

      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      } else {
        setIsEventPast(true);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCopyLocation = async () => {
    const text = 'Matrix Creation Events Venue, Malakas St., Quezon City';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-crimson-dark via-crimson to-earth-rust overflow-hidden">
        {/* Background dot pattern (matching Begin Your Journey section) */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
          {/* Event Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8 opacity-0 animate-fadeIn">
            <div className="w-2 h-2 rounded-full bg-cream animate-pulse" />
            <span className="text-sm font-medium text-white/90 tracking-wide">HOLY WEEK RETREAT 2026</span>
          </div>

          {/* Main Heading */}
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-white mb-6 tracking-tight leading-[0.95] opacity-0 animate-fadeInUp"
            style={{ animationDelay: '0.1s' }}
          >
            Greater Heights
          </h1>

          <p
            className="text-xl md:text-2xl text-cream/90 font-light mb-12 max-w-2xl mx-auto opacity-0 animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
          >
            Holy Week Retreat 2026
          </p>

          {/* Countdown / Post-Event Message */}
          <div className="mb-12 opacity-0 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            {isEventPast ? (
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-serif font-bold text-white mb-2">
                  Thank you for joining us!
                </p>
                <p className="text-lg text-cream/80">
                  Greater Heights 2026 has ended. See you next year!
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm uppercase tracking-widest text-cream/60 mb-4 font-medium">Event Begins In</p>
                <div className="flex items-center justify-center gap-4 md:gap-8">
                  <CountdownUnit value={countdown.days} label="Days" />
                  <CountdownDivider />
                  <CountdownUnit value={countdown.hours} label="Hours" />
                  <CountdownDivider />
                  <CountdownUnit value={countdown.minutes} label="Minutes" />
                  <CountdownDivider />
                  <CountdownUnit value={countdown.seconds} label="Seconds" />
                </div>
              </>
            )}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={() => document.getElementById('next-year')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-cream text-crimson-dark font-semibold rounded-lg hover:bg-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
            >
              See You Next Year
            </button>
            <button
              onClick={() => document.getElementById('next-year')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white/30 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              Save the Date 2027
            </button>
          </div>

        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 inset-x-0 flex justify-center z-10 translate-x-2">
          <div className="animate-bounce">
            <svg className="w-6 h-6 text-cream/60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Event Poster Section */}
      <section className="py-16 md:py-24 bg-white">
        <AnimatedSection>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="relative rounded-2xl overflow-hidden shadow-medium border border-earth-tan/20">
              <img
                src="/hwr-poster.jpeg"
                alt="Greater Heights Holy Week Retreat 2026 Event Poster"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </AnimatedSection>
      </section>


      {/* Event Details Section */}
      <section id="details" className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mb-20">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-earth-brown mb-6 leading-tight">
                Join us for a powerful two-day spiritual experience
              </h2>
              <p className="text-lg text-earth-brown/70 leading-relaxed">
                Experience transformative worship, inspiring biblical messages, and meaningful fellowship with the FEAST
                community during this special Holy Week gathering.
              </p>
            </div>
          </AnimatedSection>

          {/* Info Grid */}
          <StaggerContainer className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <StaggerItem>
              <InfoCard
                icon={<CalendarIcon />}
                label="Date & Time"
                title="April 2-3, 2026"
                description="Thursday - Friday, 8:00 AM - 12:00 NN"
                details="Full two-day attendance encouraged"
              />
            </StaggerItem>
            <StaggerItem>
              <InfoCard
                icon={<LocationIcon />}
                label="Venue"
                title="Matrix Creation"
                description="Malakas Street, Quezon City"
                details="Events venue with parking available"
              />
            </StaggerItem>
            <StaggerItem>
              <InfoCard
                icon={<TicketIcon />}
                label="Registration"
                title="Free Admission"
                description="One registration per person"
                details="Children included in registration"
              />
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Meet the Speakers Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-crimson-dark via-crimson to-earth-rust relative overflow-hidden">
        {/* Dot pattern — same as hero */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          {/* Header — matching poster style */}
          <AnimatedSection>
            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-white/50 text-sm font-semibold uppercase tracking-[0.4em]">Meet The</span>
            </div>
            <h2 className="text-6xl md:text-8xl font-display font-bold text-white leading-none mb-4">
              Speakers
            </h2>
            <div className="w-16 h-[3px] bg-white/40 mb-16" />
          </AnimatedSection>

          {/* Speakers row — all 4 in one row matching poster layout */}
          <StaggerContainer className="flex flex-wrap justify-center gap-8 md:gap-14 mb-16">
            {[
              { image: '/speaker1.png', name: 'Fr. Albert Garong, SSP', role: 'Speaker' },
              { image: '/speaker2.png', name: 'Bro. Obet Cabrillas', role: 'Speaker' },
              { image: '/speaker3.png', name: 'Bro. JPaul Hernandez', role: 'Speaker' },
              { image: '/speaker4.png', name: 'Fr. Franz Dizon', role: 'Speaker' },
            ].map((s, i) => (
              <StaggerItem key={i}>
                <SpeakerCard image={s.image} name={s.name} role={s.role} />
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* Host — separated below with label, matching poster */}
          <AnimatedSection>
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-[2px] bg-white/30" />
                <span className="text-white/50 text-xs font-semibold uppercase tracking-[0.5em]">Host</span>
                <div className="w-8 h-[2px] bg-white/30" />
              </div>
              <SpeakerCard
                image="/host1.png"
                name='Mr. Romar Chuca'
                role='"The Catholic Comedian"'
                isHost
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 md:py-32 overflow-hidden bg-[#f5f5f4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-earth-brown mb-4">How to Get There</h2>
              <p className="text-earth-brown/60 max-w-2xl mx-auto">Matrix Creation Events Venue, Malakas St., Quezon City</p>
            </div>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <AnimatedSection>
              {/* Route Cards */}
              <div className="space-y-2">
                <RouteCard
                  id="bus-edsa"
                  selected={selectedRoute === 'bus-edsa'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'bus-edsa' ? null : 'bus-edsa')}
                  icon={<BusIcon />}
                  title="Bus via EDSA"
                  from="SM Megamall"
                  to="GMA Kamuning"
                  duration="25-40 min"
                  fare="~₱15-40"
                  modes={['Bus', 'Walk']}
                  steps={[
                    { mode: 'bus', label: 'Northbound Bus', detail: 'Take EDSA bus heading to SM Fairview from SM Megamall' },
                    { mode: 'stop', label: 'Drop off', detail: 'Get off at JAC Liner Bus Terminal before GMA Kamuning' },
                    { mode: 'walk', label: 'Walk ~3 min', detail: 'Walk straight along Mapagmahal St., turn left to Malakas St. Venue is visible upon left turn.' },
                  ]}
                  note="Venue is 3-min walk away from drop off location."
                />
                <RouteCard
                  id="marikina"
                  selected={selectedRoute === 'marikina'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'marikina' ? null : 'marikina')}
                  icon={<JeepIcon />}
                  title="From Marikina / Katipunan"
                  from="LRT Anonas"
                  to="Matrix Events Place"
                  duration="30-50 min"
                  fare="~₱20-60"
                  modes={['LRT', 'Jeep', 'Tricycle/Walk']}
                  hasOptions={true}
                  options={[
                    {
                      label: 'Option 1 — Via Tricycle from Kamias',
                      steps: [
                        { mode: 'train', label: 'LRT Anonas (Aurora Blvd)', detail: 'From LRT Anonas, ride the jeepney going to Quiapo' },
                        { mode: 'stop', label: 'Drop off at Metrobank Kamias', detail: 'Get off at Metrobank Kamias' },
                        { mode: 'drive', label: 'Tricycle', detail: 'Take a tricycle to Matrix Events Place' },
                      ],
                    },
                    {
                      label: 'Option 2 — Walk via Mapagmahal St.',
                      steps: [
                        { mode: 'train', label: 'LRT Anonas (Aurora Blvd)', detail: 'From LRT Anonas, ride the jeepney going to Quiapo' },
                        { mode: 'stop', label: 'Drop off at Jollibee Kamias', detail: 'Get off at Jollibee Kamias corner EDSA' },
                        { mode: 'walk', label: 'Walk ~5 min', detail: 'Walk to JAC Liner, turn right to Mapagmahal St., then turn left to Malakas St. to Matrix Events Place' },
                      ],
                    },
                  ]}
                  note="Make sure the jeepney is going to Quiapo via Kamias route."
                />
                <RouteCard
                  id="trinoma"
                  selected={selectedRoute === 'trinoma'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'trinoma' ? null : 'trinoma')}
                  icon={<BusIcon />}
                  title="From Trinoma / SM North"
                  from="Trinoma"
                  to="GMA Kamuning"
                  duration="20-35 min"
                  fare="~₱15-30"
                  modes={['Bus', 'Walk']}
                  steps={[
                    { mode: 'bus', label: 'Carousel Bus', detail: 'Take the bus at Trinoma Carousel Station' },
                    { mode: 'stop', label: 'Drop off at GMA Kamuning', detail: 'Get off at GMA Kamuning Station (before the flyover)' },
                    { mode: 'walk', label: 'Walk ~5 min', detail: 'Walk towards JAC Liner, turn at Mapagmahal St., then turn left at Malakas St.' },
                  ]}
                  note="Drop off before the flyover at GMA Kamuning."
                />
                <RouteCard
                  id="quiapo"
                  selected={selectedRoute === 'quiapo'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'quiapo' ? null : 'quiapo')}
                  icon={<JeepIcon />}
                  title="From Quiapo"
                  from="Quiapo"
                  to="Jollibee Kamias"
                  duration="30-50 min"
                  fare="~₱15-30"
                  modes={['Jeep', 'Walk']}
                  steps={[
                    { mode: 'bus', label: 'Jeepney to Anonas-Proj 2/3', detail: 'Take a jeepney going to Anonas-Proj 2/3 (make sure the jeep will take the Kamuning route)' },
                    { mode: 'stop', label: 'Drop off at Jollibee Kamias', detail: 'Get off at Jollibee Kamias, right after EDSA' },
                    { mode: 'walk', label: 'Walk ~5 min', detail: 'Walk towards JAC Liner, turn right to Mapagmahal St., then turn left to Malakas St. Matrix Events Venue will be visible.' },
                  ]}
                  note="Make sure the jeepney takes the Kamuning route."
                />
                <RouteCard
                  id="commonwealth"
                  selected={selectedRoute === 'commonwealth'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'commonwealth' ? null : 'commonwealth')}
                  icon={<BusIcon />}
                  title="From Commonwealth"
                  from="Commonwealth"
                  to="EDSA / East Ave"
                  duration="40-60 min"
                  fare="~₱30-80"
                  modes={['Bus', 'Walk']}
                  hasOptions={true}
                  options={[
                    {
                      label: 'Option 1 — Via Kamuning Rd',
                      steps: [
                        { mode: 'bus', label: 'Southbound Bus', detail: 'Take a bus to EDSA via East Avenue route (like Cubao)' },
                        { mode: 'stop', label: 'Drop off at Kamuning Rd', detail: 'Cross EDSA going to Jollibee' },
                        { mode: 'walk', label: 'Walk ~5 min', detail: 'Walk straight to JAC Liner, turn right to Mapagmahal St., then turn left to Malakas St. Events place is visible.' },
                      ],
                    },
                    {
                      label: 'Option 2 — Via East Ave / Magalang St',
                      steps: [
                        { mode: 'bus', label: 'Southbound Bus', detail: 'Take a bus along East Avenue toward EDSA' },
                        { mode: 'stop', label: 'Drop off at Magalang St', detail: 'Along East Ave, after the LTO building, before reaching EDSA' },
                        { mode: 'walk', label: 'Walk ~5 min', detail: 'Walk straight then turn right to Malakas Street. Matrix venue is after the Brgy Pinyahan Hall.' },
                      ],
                    },
                  ]}
                  note="Make sure the bus takes the East Avenue route."
                />
                <RouteCard
                  id="vehicle"
                  selected={selectedRoute === 'vehicle'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'vehicle' ? null : 'vehicle')}
                  icon={<CarIcon />}
                  title="Private Vehicle"
                  from="Your location"
                  to="Matrix Creation"
                  duration="Varies"
                  fare="Gas + Parking"
                  modes={['Drive']}
                  steps={[
                    { mode: 'drive', label: 'Navigate', detail: "Search 'Matrix Creation Events, Malakas Street, Quezon City' on Waze or Google Maps" },
                  ]}
                  note="Parking available at venue."
                />
                <RouteCard
                  id="grab"
                  selected={selectedRoute === 'grab'}
                  onSelect={() => setSelectedRoute(selectedRoute === 'grab' ? null : 'grab')}
                  icon={<TaxiIcon />}
                  title="Grab / Taxi"
                  from="Your location"
                  to="Matrix Creation"
                  duration="Varies"
                  fare="App-based"
                  modes={['Ride-hail']}
                  steps={[
                    { mode: 'drive', label: 'Book a ride', detail: 'Set destination to Matrix Creation Events Venue, Malakas St., Quezon City' },
                  ]}
                  note="Share the venue address with your driver for easy navigation."
                />
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <div className="relative lg:sticky lg:top-8">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-medium border-2 border-crimson/40">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3860.405292685677!2d121.04716630000001!3d14.6329202!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397b709f4676be7%3A0x736cff4091f82ffa!2sMatrix%20Creation%20Events%20Venue!5e0!3m2!1sen!2sph!4v1770220441963!5m2!1sen!2sph"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Matrix Creation Events Venue Location"
                  />
                </div>
                {/* Venue Links */}
                <div className="mt-4 flex gap-2">
                  <a
                    href="https://maps.app.goo.gl/euoG2SieuFvaMZQp8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-crimson text-white hover:bg-crimson-dark transition-all duration-300 text-sm font-medium"
                  >
                    <LocationIcon />
                    <span>Open in Maps</span>
                  </a>
                  <button
                    onClick={handleCopyLocation}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cream border border-earth-tan/30 text-earth-brown hover:bg-earth-tan/20 transition-all duration-300 text-sm font-medium"
                  >
                    {copied ? <CheckIcon /> : <CopyIcon />}
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Guidelines Section */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-earth-brown mb-6">Event Guidelines</h2>
            </div>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <StaggerItem>
              <GuidelineCard
                title="Registration & Admission"
                guidelines={[
                  'Each registration grants one admission',
                  'Children are included in your registration',
                  'Keep your ticket accessible on your phone or printed',
                  'Registration is completely free',
                ]}
              />
            </StaggerItem>
            <StaggerItem>
              <GuidelineCard
                title="Seating Arrangements"
                guidelines={[
                  'General seating is first-come, first-served',
                  'Reserved sections for sponsors and donors',
                  'We have a separate area for seniors/pwd/pregnant. with one (1) companion',
                  'Seniors can choose to join their family out of the senior seating location if family is attending with him/her',
                ]}
              />
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Save the Date — HWR 2027 */}
      <section id="next-year" className="py-24 md:py-32 bg-gradient-to-br from-crimson-dark via-crimson to-earth-rust relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <AnimatedSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="text-sm uppercase tracking-widest text-cream/60 mb-4 font-medium">Coming Soon</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight">
              Take Courage
            </h2>
            <p className="text-lg md:text-xl text-cream/80 italic mb-3 max-w-2xl mx-auto font-light">
              &ldquo;In the world you will have trouble, but take courage, I have conquered the world.&rdquo;
            </p>
            <p className="text-cream/50 mb-10 text-sm tracking-wide">— John 16:33</p>
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/20">
              <span className="text-cream font-semibold">Holy Week Retreat 2027</span>
              <span className="text-cream/40">·</span>
              <span className="text-cream/80">March 25–26</span>
            </div>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* Footer */}
      <footer className="bg-earth-brown py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Event Official Pages */}
          <div className="mb-10 text-center">
            <p className="text-cream/80 text-sm font-medium mb-4">Follow our Official Pages</p>
            <div className="flex items-center justify-center gap-6">
              <a
                href="https://www.facebook.com/profile.php?id=61587389978654"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-col items-center gap-2 group"
              >
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-cream/30 group-hover:border-cream/60 transition-all duration-300 group-hover:scale-105 shadow-lg">
                    <img src="/hwr-logo2026.png" alt="Greater Heights HWR 2026" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1877F2] flex items-center justify-center shadow-md border-2 border-earth-brown">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-cream font-semibold text-xs group-hover:text-white transition-colors">Facebook</p>
                </div>
              </a>
              <a
                href="https://www.instagram.com/holyweekretreat_farm/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-col items-center gap-2 group"
              >
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-cream/30 group-hover:border-cream/60 transition-all duration-300 group-hover:scale-105 shadow-lg">
                    <img src="/hwr-logo2026.png" alt="Greater Heights HWR 2026" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#E1306C] flex items-center justify-center shadow-md border-2 border-earth-brown">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-cream font-semibold text-xs group-hover:text-white transition-colors">Instagram</p>
                </div>
              </a>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-12 pt-8 border-t border-earth-tan/20">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-display font-bold text-cream mb-2">Greater Heights</h3>
              <p className="text-earth-tan">Holy Week Retreat 2026</p>
            </div>

            <div className="flex items-center gap-6">
              <SocialLink href="https://www.facebook.com/TheFeastMagnolia" label="Feast Magnolia Facebook" icon={<FacebookIcon />} />
              <SocialLink
                href="https://www.instagram.com/thefeastmagnolia"
                label="Instagram"
                icon={<InstagramIcon />}
              />
              <SocialLink href="mailto:feastmagnolia@gmail.com" label="Email" icon={<EmailIcon />} />
              <SocialLink href="https://feast.ph/" label="Website" icon={<WebsiteIcon />} />
            </div>
          </div>

          <div className="text-center text-earth-tan/70 text-sm">
            <p>&copy; 2026 Feast Magnolia. All rights reserved.</p>
          </div>
          <div className="mt-4 text-center text-earth-tan/40 text-xs">
            <p>by <a href="https://marshnandez-connect-hub.vercel.app/" target="_blank" rel="noopener noreferrer" className="hover:text-cream transition-colors underline underline-offset-2">Marshnandez</a>-Dev</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Countdown Components ──────────────────────────────────────────────────────

const CountdownUnit = ({ value, label }: { value: number; label: string }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-light text-white font-display mb-1 tabular-nums">
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-xs uppercase tracking-widest text-cream/50 font-medium">{label}</div>
  </div>
);

const CountdownDivider = () => <div className="text-3xl md:text-4xl text-cream/30 font-light">:</div>;

// ─── Speaker Card ──────────────────────────────────────────────────────────────

const SpeakerCard = ({ image, name, role, isHost = false }: { image: string; name: string; role: string; isHost?: boolean }) => (
  <div className={`flex flex-col items-center text-center ${isHost ? 'w-44 md:w-52' : 'w-36 md:w-44'}`}>
    <div className="relative mb-5">
      {/* Outer crimson glow ring */}
      <div className={`${isHost ? 'w-40 h-40 md:w-48 md:h-48' : 'w-32 h-32 md:w-40 md:h-40'} rounded-full p-[3px] bg-gradient-to-br from-crimson via-[#e05555] to-crimson shadow-[0_0_30px_rgba(180,60,58,0.5)]`}>
        {/* White inner border */}
        <div className="w-full h-full rounded-full p-[4px] bg-white">
          <div className="w-full h-full rounded-full overflow-hidden">
            <img src={image} alt={name} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>
    <h3 className="text-white font-bold text-sm md:text-base leading-snug">{name}</h3>
    <p className="text-cream/70 text-xs mt-0.5 italic">{role}</p>
  </div>
);

// ─── Info Card (Redesigned) ────────────────────────────────────────────────────

const InfoCard = ({
  icon,
  label,
  title,
  description,
  details,
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  description: string;
  details: string;
}) => (
  <div className="group relative bg-white rounded-2xl p-7 border border-earth-tan/20 hover:border-crimson/30 transition-all duration-300 hover:shadow-medium overflow-hidden">
    {/* Left accent bar */}
    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-crimson rounded-l-2xl" />
    {/* Icon — crimson, no box */}
    <div className="text-crimson mb-5 transition-transform duration-300 group-hover:scale-110 w-fit">
      {icon}
    </div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-crimson/70 font-bold mb-1">{label}</p>
    <h3 className="text-2xl font-display font-bold text-earth-brown mb-2 leading-tight">{title}</h3>
    <p className="text-earth-brown/70 text-sm leading-relaxed mb-1">{description}</p>
    <p className="text-xs text-earth-brown/45">{details}</p>
  </div>
);

// ─── Route Card Component (Sakay.ph-inspired) ────────────────────────────────

type RouteStep = { mode: string; label: string; detail: string };
type RouteOption = { label: string; steps: RouteStep[] };

const RouteCard = ({
  selected,
  onSelect,
  icon,
  title,
  from,
  to,
  duration,
  fare,
  steps,
  options,
  hasOptions,
  note,
}: {
  id: string;
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  from: string;
  to: string;
  duration: string;
  fare: string;
  modes?: string[];
  steps?: RouteStep[];
  options?: RouteOption[];
  hasOptions?: boolean;
  note: string;
}) => {
  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all duration-200 bg-white ${selected ? 'border-crimson shadow-md' : 'border-earth-tan/30 hover:border-crimson/30 hover:shadow-soft'}`}>
      {/* Summary Card (always visible) */}
      <button onClick={onSelect} className="w-full text-left px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          {/* Icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-earth-brown text-cream flex items-center justify-center">
            {icon}
          </div>

          {/* Route info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-earth-brown text-sm truncate">{title}</h4>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px] text-earth-brown/50 truncate">{from} → {to}</span>
              <span className="text-[10px] font-semibold text-earth-brown/70 bg-cream px-1.5 py-0.5 rounded flex-shrink-0">{duration}</span>
              <span className="text-[10px] font-medium text-crimson flex-shrink-0">{fare}</span>
            </div>
          </div>

          {/* Expand indicator */}
          <motion.div
            animate={{ rotate: selected ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0 text-earth-brown/40"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </motion.div>
        </div>
      </button>

      {/* Expanded step-by-step directions */}
      <AnimatePresence initial={false}>
        {selected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 border-t border-earth-tan/20 pt-3">
              {hasOptions && options ? (
                <div className="space-y-3">
                  {options.map((option, oi) => (
                    <div key={oi}>
                      <h5 className="text-[11px] font-bold text-crimson uppercase tracking-wider mb-2">{option.label}</h5>
                      <RouteSteps steps={option.steps} />
                    </div>
                  ))}
                </div>
              ) : (
                steps && <RouteSteps steps={steps} />
              )}
              {/* Note */}
              <div className="mt-3 flex items-start gap-1.5 bg-cream/60 rounded-md p-2 border border-earth-tan/20">
                <svg className="w-3.5 h-3.5 text-crimson flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[11px] text-earth-brown/60 italic">{note}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const modeColors: Record<string, string> = {
  train: 'bg-crimson',
  bus: 'bg-crimson/80',
  walk: 'bg-earth-brown',
  transfer: 'bg-earth-brown/70',
  stop: 'bg-crimson-dark',
  drive: 'bg-earth-brown',
};

const RouteSteps = ({ steps }: { steps: RouteStep[] }) => (
  <div className="relative">
    {steps.map((step, i) => (
      <div key={i} className="flex items-start gap-2 relative">
        {/* Vertical line connecting dots */}
        {i < steps.length - 1 && (
          <div className="absolute left-[9px] top-5 w-0.5 h-[calc(100%-6px)] bg-earth-tan/30" />
        )}
        {/* Dot */}
        <div className={`flex-shrink-0 w-[18px] h-[18px] rounded-full ${modeColors[step.mode] || 'bg-earth-brown'} flex items-center justify-center z-10 mt-0.5`}>
          <div className="w-1.5 h-1.5 rounded-full bg-white" />
        </div>
        {/* Content */}
        <div className="pb-2.5">
          <p className="text-[11px] font-semibold text-earth-brown">{step.label}</p>
          <p className="text-[11px] text-earth-brown/60 leading-snug">{step.detail}</p>
        </div>
      </div>
    ))}
  </div>
);

// ─── Guideline Card ────────────────────────────────────────────────────────────

const GuidelineCard = ({ title, guidelines }: { title: string; guidelines: string[] }) => (
  <div className="bg-white rounded-xl p-8 border border-earth-tan/30 hover:shadow-soft transition-shadow duration-300">
    <h3 className="text-2xl font-display font-bold text-earth-brown mb-6">{title}</h3>
    <ul className="space-y-4">
      {guidelines.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-earth-brown/70">
          <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-crimson mt-2" />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ─── Social Link ───────────────────────────────────────────────────────────────

const SocialLink = ({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-10 h-10 rounded-lg bg-earth-tan/20 hover:bg-cream flex items-center justify-center text-earth-tan hover:text-crimson transition-all duration-300"
    aria-label={label}
  >
    {icon}
  </a>
);

// ─── Icons ─────────────────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
    />
  </svg>
);

const LocationIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
    />
  </svg>
);

const TicketIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
    />
  </svg>
);


const CarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
    />
  </svg>
);

const BusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125V6.375c0-.621-.504-1.125-1.125-1.125h-17.25c-.621 0-1.125.504-1.125 1.125v10.5c0 .621.504 1.125 1.125 1.125M18 9h-3m3 3h-3m3 3h-3"
    />
  </svg>
);

const TaxiIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125V6.375c0-.621-.504-1.125-1.125-1.125h-17.25c-.621 0-1.125.504-1.125 1.125v10.5c0 .621.504 1.125 1.125 1.125M9 3.75h6"
    />
  </svg>
);

const JeepIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="8" width="18" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 8V6a2 2 0 012-2h6a2 2 0 012 2v2M7 17v2m10-2v2M5 13h14" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
  </svg>
);

const WebsiteIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);
